/**
 * 3D marker renderer using Marker3DInteractiveElement.
 * Markers are partitioned into named layers, appended as children of the
 * Map3DElement. 3D has no clusterer, so visibility toggles append/remove.
 */

import { buildPopupHtml, isFeedTrack } from './marker-popup';
import { attachClassificationListeners } from './classification-popup';
import { lucideImageElement, lucideSvgString } from '../lucide-marker-icons';
import { selectEntity } from './selection';
import type { MarkerData, MarkerDiff } from './marker-state';

/** Dark glyph color so the pictorial icon contrasts the colored pin head. */
const GLYPH_FILL = '#0b1117';

/** On-screen size (px) of a pictorial 3D marker icon. */
const MARKER_ICON_PX = 30;

type Marker3D = google.maps.maps3d.Marker3DInteractiveElement;

/** Per-layer marker registries keyed by layer, then by UID. */
const layerMarkers = new Map<string, Map<string, Marker3D>>();

/** Per-layer visibility (defaults to visible). */
const layerVisible = new Map<string, boolean>();

/** Custom popup overlay element */
let popupOverlay: HTMLDivElement | null = null;

/** Active map3d reference */
let activeMap3d: google.maps.maps3d.Map3DElement | null = null;

/** Initialize 3D marker system */
export function initMarkers3D(map3d: google.maps.maps3d.Map3DElement): void {
  activeMap3d = map3d;
  popupOverlay = createPopupOverlay(map3d);
}

/** Apply a diff of markers for a layer to the 3D map */
export function applyLayerDiff3D(
  map3d: google.maps.maps3d.Map3DElement,
  layer: string,
  diff: MarkerDiff,
): void {
  const registry = getLayerMarkers(layer);
  const visible = isLayerVisible(layer);
  removeMarkers3D(diff.removed, registry, map3d);
  reclassifyMarkers3D(diff.reclassified, registry, map3d, visible);
  createMarkers3D(diff.added, registry, map3d, visible);
  updatePositions3D(diff.updated, registry);
}

/** Re-create 3D markers whose symbol changed (classification override) */
function reclassifyMarkers3D(
  markers: MarkerData[],
  registry: Map<string, Marker3D>,
  map3d: google.maps.maps3d.Map3DElement,
  visible: boolean,
): void {
  if (markers.length === 0) return;
  removeMarkers3D(markers.map((m) => m.uid), registry, map3d);
  createMarkers3D(markers, registry, map3d, visible);
}

/** Set marker positions for a layer (used by animation frames) */
export function setLayerPositions3D(
  layer: string,
  positions: Map<string, google.maps.LatLngLiteral>,
): void {
  const registry = layerMarkers.get(layer);
  if (!registry) return;
  for (const [uid, pos] of positions) {
    const marker = registry.get(uid);
    if (marker) marker.position = { lat: pos.lat, lng: pos.lng, altitude: currentAltitude(marker) };
  }
}

/** Read a 3D marker's current altitude, defaulting to ground level. */
function currentAltitude(marker: Marker3D): number {
  return marker.position?.altitude ?? 0;
}

/** Show or hide a layer's markers */
export function setLayerVisibility3D(layer: string, visible: boolean): void {
  layerVisible.set(layer, visible);
  const registry = layerMarkers.get(layer);
  if (!registry || !activeMap3d) return;
  for (const marker of registry.values()) {
    toggleChild(marker, activeMap3d, visible);
  }
}

/** Destroy all 3D markers */
export function destroyMarkers3D(): void {
  for (const registry of layerMarkers.values()) clearMarkerMap3D(registry);
  layerMarkers.clear();
  layerVisible.clear();
  removePopupOverlay();
  activeMap3d = null;
}

/* ─── Internal ─── */

function getLayerMarkers(layer: string): Map<string, Marker3D> {
  let registry = layerMarkers.get(layer);
  if (!registry) {
    registry = new Map();
    layerMarkers.set(layer, registry);
  }
  return registry;
}

function isLayerVisible(layer: string): boolean {
  return layerVisible.get(layer) ?? true;
}

function toggleChild(
  marker: Marker3D,
  map3d: google.maps.maps3d.Map3DElement,
  visible: boolean,
): void {
  if (visible) {
    map3d.appendChild(marker);
    return;
  }
  try {
    map3d.removeChild(marker);
  } catch {
    /* marker may already be detached */
  }
}

function createMarkers3D(
  markers: MarkerData[],
  registry: Map<string, Marker3D>,
  map3d: google.maps.maps3d.Map3DElement,
  visible: boolean,
): void {
  for (const data of markers) {
    const placement = positionFor(data.position, data.altitude);
    const elevated = data.altitude !== undefined;
    const marker = new google.maps.maps3d.Marker3DInteractiveElement({
      position: placement.position,
      altitudeMode: placement.altitudeMode,
      // Ground markers extrude; elevated (orbital) markers float free and keep a
      // constant on-screen size so they stay visible at orbital distance.
      extruded: !elevated,
      sizePreserved: elevated,
      label: data.simulated ? `SIM · ${data.callsign}` : data.callsign,
    });

    marker.append(buildMarkerVisual(data.color, data.icon));
    marker.addEventListener('gmp-click', () => showPopup3D(data));

    registry.set(data.uid, marker);
    if (visible) map3d.appendChild(marker);
  }
}

/**
 * Build a 3D marker visual. Feed layers with a pictorial icon (satellite,
 * aircraft, vessel, fire, cctv, …) render that icon directly — affiliation-
 * colored, drop-shadowed for legibility — so each data source is instantly
 * distinguishable instead of an identical pin. Layers without an icon
 * (TAK contacts/CoT) keep the affiliation-colored Google pin.
 */
function buildMarkerVisual(color: string, icon: string | undefined): HTMLElement {
  const svg = markerIconSvg(icon, color);
  if (!svg) return buildPin(color, icon);
  // Marker3DInteractiveElement requires the <template> content to be directly an
  // <svg>/<img> (no wrapping element), so we render a self-contained SVG that
  // bakes in a dark drop-shadow for legibility over imagery.
  const template = document.createElement('template');
  template.innerHTML = svg;
  return template;
}

/** A self-contained, drop-shadowed SVG for a pictorial marker icon, or undefined. */
function markerIconSvg(icon: string | undefined, color: string): string | undefined {
  const inner = lucideSvgString(icon, color, MARKER_ICON_PX);
  if (!inner) return undefined;
  const pad = 4;
  const box = MARKER_ICON_PX + pad * 2;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${box}" height="${box}" viewBox="0 0 ${box} ${box}">`,
    '<defs><filter id="mshadow" x="-50%" y="-50%" width="200%" height="200%">',
    '<feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#0b1117" flood-opacity="0.95"/>',
    '</filter></defs>',
    `<g filter="url(#mshadow)" transform="translate(${pad},${pad})">${inner}</g>`,
    '</svg>',
  ].join('');
}

/**
 * Build a colored 3D pin for icon-less layers (TAK contacts/CoT). 3D PinElements
 * are colored by affiliation; the 2D map keeps the full MIL-STD symbology.
 */
function buildPin(color: string, icon: string | undefined): google.maps.marker.PinElement {
  return new google.maps.marker.PinElement({
    background: color,
    borderColor: '#0b0e14',
    glyph: lucideImageElement(icon, GLYPH_FILL),
    glyphColor: '#0b0e14',
    scale: 1.1,
  });
}

function removeMarkers3D(
  uids: string[],
  registry: Map<string, Marker3D>,
  map3d: google.maps.maps3d.Map3DElement,
): void {
  for (const uid of uids) {
    const marker = registry.get(uid);
    if (!marker) continue;
    detachMarker(marker, map3d);
    registry.delete(uid);
  }
}

function detachMarker(marker: Marker3D, map3d: google.maps.maps3d.Map3DElement): void {
  try {
    map3d.removeChild(marker);
  } catch {
    /* marker may already be detached */
  }
}

function updatePositions3D(markers: MarkerData[], registry: Map<string, Marker3D>): void {
  for (const data of markers) {
    const marker = registry.get(data.uid);
    if (!marker) continue;
    const placement = positionFor(data.position, data.altitude);
    marker.position = placement.position;
    marker.altitudeMode = placement.altitudeMode;
  }
}

/** Placement (position + altitude mode) for a marker: absolute shell if elevated, else clamp to ground. */
function positionFor(
  pos: google.maps.LatLngLiteral,
  altitude: number | undefined,
): { position: google.maps.LatLngAltitudeLiteral; altitudeMode: google.maps.maps3d.AltitudeMode } {
  if (altitude === undefined) {
    return {
      position: { lat: pos.lat, lng: pos.lng, altitude: 0 },
      altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    };
  }
  return {
    position: { lat: pos.lat, lng: pos.lng, altitude },
    altitudeMode: google.maps.maps3d.AltitudeMode.ABSOLUTE,
  };
}

function clearMarkerMap3D(registry: Map<string, Marker3D>): void {
  if (activeMap3d) {
    for (const marker of registry.values()) detachMarker(marker, activeMap3d);
  }
  registry.clear();
}

function createPopupOverlay(map3d: google.maps.maps3d.Map3DElement): HTMLDivElement {
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:absolute',
    'display:none',
    'top:50%',
    'left:50%',
    'transform:translate(-50%,-50%)',
    'background:rgba(20,20,30,0.92)',
    'color:#e0e0e0',
    'border-radius:8px',
    'box-shadow:0 4px 16px rgba(0,0,0,0.5)',
    'padding:10px 14px',
    'z-index:1000',
    'max-width:260px',
    'pointer-events:auto',
  ].join(';');

  const closeBtn = document.createElement('button');
  closeBtn.textContent = '×';
  closeBtn.style.cssText = [
    'position:absolute',
    'top:4px',
    'right:8px',
    'background:none',
    'border:none',
    'color:#888',
    'font-size:16px',
    'cursor:pointer',
  ].join(';');
  closeBtn.addEventListener('click', () => hidePopup());
  overlay.appendChild(closeBtn);

  map3d.parentElement?.appendChild(overlay);
  return overlay;
}

function showPopup3D(data: MarkerData): void {
  if (!popupOverlay) return;
  selectEntity(data);
  const contentDiv = popupOverlay.querySelector('.popup-content') ?? createContentDiv();
  contentDiv.innerHTML = buildPopupHtml(data);
  popupOverlay.style.display = 'block';
  if (isFeedTrack(data.layer)) attachClassificationListeners(data.uid);
}

function hidePopup(): void {
  if (popupOverlay) popupOverlay.style.display = 'none';
}

function createContentDiv(): HTMLDivElement {
  if (!popupOverlay) throw new Error('Popup overlay not initialized');
  const div = document.createElement('div');
  div.className = 'popup-content';
  popupOverlay.appendChild(div);
  return div;
}

function removePopupOverlay(): void {
  popupOverlay?.remove();
  popupOverlay = null;
}
